/**
 * NoteHighlight: marks the words a note is anchored to, inside the manuscript (cockatrice-ui `src/ui/NoteHighlight.jsx`,
 * ported to hyperscript; PLAN.md §0).
 * Private notes use a dotted underline, editors' notes plum. Clickable when onClick is passed.
 */
import './css/note-highlight.css';
import { mergeProps, splitProps } from 'solid-js';
import { el } from './solid.ts';
import type { Attributes, Child } from './solid.ts';
import { uiText } from './text.ts';

export interface NoteHighlightProps extends Attributes {
  /** Highlight style. Default "shared". */
  readonly kind?: 'shared' | 'private' | 'editor' | undefined;
  /** The note open in the panel. */
  readonly active?: boolean | undefined;
  /** Makes the mark focusable and clickable. */
  readonly onClick?: ((e: Event) => void) | undefined;
  /** Accessible name when clickable. */
  readonly label?: string | undefined;
  /** The anchored words. */
  readonly children?: Child;
  /** Extra class. */
  readonly class?: string | undefined;
}

const NAMES = { shared: 'ui.note-highlight.shared', private: 'ui.note-highlight.private', editor: 'ui.note-highlight.editor' } as const;

export default function NoteHighlight(props: NoteHighlightProps): Child {
  const [local, rest] = splitProps(props, ['kind', 'active', 'onClick', 'label', 'children', 'class']);
  const kind = (): 'shared' | 'private' | 'editor' => local.kind ?? 'shared';
  const clickable = (): boolean => local.onClick !== undefined;
  // `{...rest}` comes last in the handoff's JSX, so what the caller passes wins over the mark's own attributes.
  return el(
    'mark',
    mergeProps(
      {
        class: () =>
          `ui-note-highlight ui-note-highlight--${kind()}${local.active === true ? ' is-active' : ''}${local.class !== undefined && local.class !== '' ? ` ${local.class}` : ''}`,
        role: () => (clickable() ? 'button' : undefined),
        tabindex: () => (clickable() ? 0 : undefined),
        'aria-label': () => (clickable() ? local.label !== undefined && local.label !== '' ? local.label : uiText()(NAMES[kind()]) : undefined),
        onClick: (e: MouseEvent) => {
          local.onClick?.(e);
        },
        onKeyDown: (e: KeyboardEvent) => {
          const open = local.onClick;
          if (open === undefined) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open(e);
          }
        },
      },
      rest,
    ),
    () => local.children,
  );
}
